import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useSnackbar } from "notistack";
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Tooltip,
  Typography,
  makeStyles,
} from "@material-ui/core";
import GetAppIcon from "@material-ui/icons/GetApp";
import FileCopyOutlinedIcon from "@material-ui/icons/FileCopyOutlined";
import CheckCircleOutlineIcon from "@material-ui/icons/CheckCircleOutline";
import dayjs from "dayjs";
import type { MalwareContent, MalwareFile } from "@dashboard-v3/api";
import DetailFields, { Field } from "./DetailFields";
import DetailSection from "./DetailSection";
import DownloadWarningDialog from "./DownloadWarningDialog";
import EmailMetadataSection from "./EmailMetadataSection";

type Props = {
  file: MalwareFile;
  content: MalwareContent;
  onClose: () => void;
};

export default function FileDetailModal({ file, content, onClose }: Props) {
  const { t } = useTranslation("mxheroShares");
  const { enqueueSnackbar } = useSnackbar();
  const classes = useStyles();
  const [downloadOpen, setDownloadOpen] = useState(false);

  async function copyChecksum() {
    try {
      await navigator.clipboard.writeText(content.checksum);
      enqueueSnackbar(t("malware.detail.checksumCopied"), {
        variant: "success",
      });
    } catch (error) {
      enqueueSnackbar(t("malware.errors.copy"), { variant: "error" });
    }
  }

  const detectedAt = content.detectedAt
    ? dayjs(content.detectedAt).format("YYYY-MM-DD HH:mm")
    : t("malware.list.unknownDate");

  const fileFields: Field[] = [
    { label: t("malware.detail.name"), value: file.name },
    { label: t("malware.detail.path"), value: file.path },
    { label: t("malware.detail.user"), value: file.user },
    {
      label: t("malware.detail.updated"),
      value: dayjs(file.updatedAt).format("YYYY-MM-DD HH:mm"),
    },
    {
      label: t("malware.detail.checksum"),
      value: (
        <Box display="flex" alignItems="center" gridGap={4}>
          <span className={classes.mono}>{content.checksum}</span>
          <Tooltip title={t("common:copy")}>
            <IconButton size="small" onClick={copyChecksum}>
              <FileCopyOutlinedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];

  const scanFields: Field[] = [
    {
      label: t("malware.detail.threat"),
      value: (
        <Chip
          size="small"
          variant="outlined"
          label={content.threat}
          className={classes.threatChip}
        />
      ),
    },
    { label: t("malware.detail.detected"), value: detectedAt },
  ];

  return (
    <>
      <Dialog open onClose={onClose} fullWidth maxWidth="md">
        <DialogTitle>{file.name}</DialogTitle>
        <DialogContent>
          {content.allowedToUse && (
            <div className={classes.ignoredAlert}>
              <CheckCircleOutlineIcon fontSize="small" />
              <Typography variant="body2">
                {t("malware.detail.ignoredAlert", { user: content.allowedBy })}
              </Typography>
            </div>
          )}
          <DetailSection title={t("malware.detail.fileSection")}>
            <DetailFields fields={fileFields} />
          </DetailSection>
          <DetailSection title={t("malware.detail.scanSection")}>
            <DetailFields fields={scanFields} />
          </DetailSection>
          <DetailSection title={t("malware.detail.emailSection")}>
            <EmailMetadataSection fileId={file.id} />
          </DetailSection>
        </DialogContent>
        <DialogActions>
          <Button
            color="primary"
            variant="outlined"
            startIcon={<GetAppIcon />}
            data-testid="malware-download-btn"
            onClick={() => setDownloadOpen(true)}
          >
            {t("malware.actions.download")}
          </Button>
          <Box flex={1} />
          <Button onClick={onClose}>{t("common:close")}</Button>
        </DialogActions>
      </Dialog>
      {downloadOpen && (
        <DownloadWarningDialog
          file={file}
          content={content}
          onClose={() => setDownloadOpen(false)}
        />
      )}
    </>
  );
}

const useStyles = makeStyles({
  mono: {
    fontFamily: "SFMono-Regular, Consolas, Menlo, monospace",
    fontSize: "0.78rem",
  },
  threatChip: { color: "#b3261e", borderColor: "rgba(244, 67, 54, 0.5)" },
  ignoredAlert: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    padding: "12px 14px",
    marginBottom: 20,
    borderRadius: 4,
    backgroundColor: "#e6f4ec",
    color: "#1e4620",
    "& svg": { color: "#2e7d52" },
  },
});
